import { useState, useRef, useEffect, useCallback } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { uploadBlob, captureVideoPoster } from "../lib/media";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Video, Circle, Square, RotateCcw, Check, Shuffle, Loader2, VideoOff } from "lucide-react";
import { toast } from "sonner";

const PROMPTS = [
  "Qual è il ricordo più felice della tua vita?",
  "Cosa vorresti che i tuoi cari non dimenticassero mai di te?",
  "Racconta come hai conosciuto l'amore della tua vita.",
  "Qual è il consiglio più importante che vuoi lasciare?",
  "Descrivi la tua giornata perfetta.",
  "Cosa ti rende più orgoglioso della tua famiglia?",
  "Quale tradizione vorresti che continuasse dopo di te?",
  "Racconta un momento difficile e come l'hai superato.",
  "Qual è la cosa che ti ha fatto ridere di più?",
  "Che cosa significa per te la parola «casa»?",
];

const pickPrompt = (exclude) => {
  const pool = PROMPTS.filter((p) => p !== exclude);
  return pool[Math.floor(Math.random() * pool.length)];
};

const VideoRecorder = ({ open, onOpenChange, onSave }) => {
  const liveRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [ready, setReady] = useState(false);
  const [denied, setDenied] = useState(false);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [prompt, setPrompt] = useState(PROMPTS[0]);
  const [uploading, setUploading] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setDenied(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (liveRef.current) {
        liveRef.current.srcObject = stream;
        await liveRef.current.play().catch(() => {});
      }
      setReady(true);
    } catch (e) {
      setDenied(true);
      setReady(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setPrompt(pickPrompt());
      startCamera();
    } else {
      stopStream();
      setReady(false); setRecording(false); setBlob(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null); setSeconds(0);
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!recording) return;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [recording]);

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : "video/webm";
    const rec = new MediaRecorder(streamRef.current, { mimeType: mime });
    rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
    rec.onstop = () => {
      const b = new Blob(chunksRef.current, { type: "video/webm" });
      setBlob(b);
      setPreviewUrl(URL.createObjectURL(b));
    };
    recorderRef.current = rec;
    rec.start();
    setSeconds(0);
    setRecording(true);
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setBlob(null); setPreviewUrl(null); setSeconds(0);
    if (liveRef.current && streamRef.current) {
      liveRef.current.srcObject = streamRef.current;
      liveRef.current.play().catch(() => {});
    }
  };

  const save = async () => {
    if (!blob) return;
    setUploading(true);
    try {
      const data = await uploadBlob(blob, `videomessaggio-${Date.now()}.webm`);
      const posterBlob = await captureVideoPoster(blob);
      if (posterBlob) {
        const p = await uploadBlob(posterBlob, `poster-${Date.now()}.jpg`);
        data.poster = p.storage_path;
      }
      onSave(data);
      toast.success("Videomessaggio salvato");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel salvataggio");
    } finally {
      setUploading(false);
    }
  };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-2xl max-w-2xl" data-testid="video-recorder-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Video className="w-6 h-6 text-primary" /> Registra un videomessaggio
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-[#FBEDE7] rounded-xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-primary mb-1">Spunto</p>
                <p className="font-serif text-lg text-[#2C2A29]" data-testid="recorder-prompt">{prompt}</p>
              </div>
              <button onClick={() => setPrompt(pickPrompt(prompt))} className="shrink-0 inline-flex items-center gap-1.5 text-sm text-primary hover:underline" data-testid="recorder-shuffle-prompt">
                <Shuffle className="w-4 h-4" /> Cambia
              </button>
            </div>
          </div>

          <div className="relative rounded-2xl overflow-hidden bg-[#2C2A29] aspect-video flex items-center justify-center">
            {denied ? (
              <div className="text-center text-white/80 px-6">
                <VideoOff className="w-10 h-10 mx-auto mb-3 opacity-70" />
                <p className="text-sm">Non riusciamo ad accedere alla fotocamera. Consenti l'accesso e riprova.</p>
                <Button onClick={startCamera} className="mt-4 rounded-full bg-primary hover:bg-[#c9694f] text-white" data-testid="recorder-retry-camera">Riprova</Button>
              </div>
            ) : previewUrl ? (
              <video src={previewUrl} controls className="w-full h-full object-cover" data-testid="recorder-preview" />
            ) : (
              <video ref={liveRef} muted playsInline className="w-full h-full object-cover" data-testid="recorder-live" />
            )}
            {recording && (
              <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" /> REC {fmt(seconds)}
              </div>
            )}
          </div>

          <div className="flex items-center justify-center gap-3">
            {!previewUrl ? (
              !recording ? (
                <Button onClick={startRecording} disabled={!ready} className="rounded-full bg-red-500 hover:bg-red-600 text-white h-12 px-8" data-testid="recorder-start-btn">
                  <Circle className="w-4 h-4 mr-2 fill-white" /> Inizia a registrare
                </Button>
              ) : (
                <Button onClick={stopRecording} className="rounded-full bg-[#2C2A29] hover:bg-black text-white h-12 px-8" data-testid="recorder-stop-btn">
                  <Square className="w-4 h-4 mr-2 fill-white" /> Ferma
                </Button>
              )
            ) : (
              <>
                <Button onClick={retake} variant="outline" className="rounded-full border-border h-12 px-6" data-testid="recorder-retake-btn">
                  <RotateCcw className="w-4 h-4 mr-2" /> Rifai
                </Button>
                <Button onClick={save} disabled={uploading} className="rounded-full bg-primary hover:bg-[#c9694f] text-white h-12 px-8" data-testid="recorder-save-btn">
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Usa questo video
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VideoRecorder;
